import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getErrorCode, getErrorMessage } from 'helpers/api-helper';
import { verifyMfaOtp } from 'helpers/mfa-helper';
import { userContext } from 'components/contexts/auth/auth.context';
import { IAuthenticateRes } from 'interfaces/auth.interface';
import { MfaChallengeAction } from '@anghami/neogateway/dist/endpoints/authentication';
import MfaChallenge from 'components/Landing/frames/MfaChallenge';

interface IMfaLocationState {
  action: MfaChallengeAction;
  mfaToken: string;
  otpauthUrl?: string | null;
}

export default function Mfa() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { handleAuthSuccess } = React.useContext(userContext);
  const state = location.state as IMfaLocationState | null;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!state?.mfaToken) {
      navigate(`/login${location.search}`);
    }
  }, []);

  if (!state?.mfaToken) return null;

  const handleSubmit = (otp: string) => {
    setLoading(true);
    setError('');
    verifyMfaOtp(state.mfaToken, otp)
      .then(({ data }) => {
        setLoading(false);
        if (!data || data.error || !data.authenticate) {
          const code = getErrorCode(data?.error);
          if (code === 60) {
            navigate(`/login${location.search}`);
            return;
          }
          setError(
            getErrorMessage(
              data?.error,
              code === 62
                ? t('Invalid or expired code, please try again.')
                : t('Something went wrong, please try again.')
            )
          );
          return;
        }
        const authObject: IAuthenticateRes = data.authenticate._attributes || data.authenticate;
        handleAuthSuccess(authObject);
      })
      .catch(() => {
        setLoading(false);
        setError(t('Something went wrong, please try again.'));
      });
  };

  return (
    <div className="h-100 w-100 p-2 position-relative f-column f-justify-center f-align-center">
      <MfaChallenge
        action={state.action}
        otpauthUrl={state.otpauthUrl}
        loading={loading}
        error={error}
        onSubmit={handleSubmit}
        onBack={() => navigate(`/login${location.search}`)}
      />
    </div>
  );
}
